import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Tool } from '@cyborg-monorepo/hunter/common';
import {
  categories,
  CCM_CATEGORIES,
  CCM_FIELDS,
  Environment,
  fields,
  MappingSupport,
  SupportedMappings,
  toolFactory,
} from '@cyborg-monorepo/hunter/matchboi';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { switchMap } from 'rxjs';
import { getToolIcon, supportedToolUrl } from '../../../../../helpers/utils';
import { EnvironmentService } from '../../../../shared/services/environment.service';
import { ToolService } from '../../../../shared/services/tool.service';

@Component({ standalone: false, 
  templateUrl: './tool.component.html',
  styleUrls: ['./tool.component.scss'],
})
export class ToolComponent implements OnInit, AfterViewChecked {
  constructor(
    private route: ActivatedRoute,
    private toolService: ToolService,
    private environmentService: EnvironmentService,
    private router: Router,
    private snackBarService: MatSnackBar,
    private formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}
  public isEditing = false;
  public toolForm: FormGroup;
  public tool: Tool;
  public customMapping: boolean = false;
  public getToolIcon = getToolIcon;
  public supportedToolUrl = supportedToolUrl;

  public data: Array<MappingSupport> = SupportedMappings;

  public groupedData: GroupResult[] = groupBy(this.data, [
    { field: 'category' },
  ]) as GroupResult[];

  public categories: CCM_CATEGORIES = categories;
  public categoryKeys = Object.keys(categories);
  public fields: CCM_FIELDS = fields;
  public newTool: boolean = false;
  public environment: Environment;
  public categoryData = [];
  public openIndexes: Set<number> = new Set();

  public ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params: Params) => {
          return this.toolService.getToolById(params.toolId);
        }),
      )
      .subscribe((tool: Tool) => {
        this.tool = tool;

        // check for invalid categories
        this.tool.fieldMappings
          .filter((mapping) => {
            return this.categoryKeys.includes(mapping.category);
          })
          .forEach((mapping, index) => {
            const group = this.formBuilder.group({
              name: new FormControl(mapping.name, [Validators.required]),
              category: new FormControl(mapping.category, [
                Validators.required,
              ]),
              fields: this.formBuilder.array([]),
              toolSpecific: this.formBuilder.array([]),
            });

            const toolSpecific = group.get('toolSpecific') as FormArray;

            if (mapping.toolSpecific)
              mapping.toolSpecific.forEach((field) => {
                toolSpecific.push(
                  this.formBuilder.group({
                    [Object.keys(field)[0]]: new FormControl(
                      Object.values(field)[0],
                      [Validators.required],
                    ),
                  }),
                );
              });

            this.fieldMappings().push(group);

            const fields = this.fieldMappings()
              .at(index)
              .get('fields') as FormArray;

            this.categories[mapping.category].ccm_fields.forEach((field) => {
              const mappedField: { [ccmField: string]: string }[] =
                mapping.fields.filter((mapField) => {
                  return Object.keys(mapField)[0] === field;
                });
              if (this.fields[field]) {
                fields.push(
                  this.formBuilder.group({
                    [field]:
                      mappedField && mappedField.length > 0
                        ? Object.values(mappedField[0])[0]
                        : '',
                  }),
                );
              }
            });
          });

        const selectedTool = this.data.filter((tool) => {
          return tool.name === this.tool?.product;
        });

        if (selectedTool) {
          this.customMapping = selectedTool[0].customMapping;

          this.categoryData = [];
          if (
            selectedTool[0].allowedCategories &&
            selectedTool[0].allowedCategories.length > 0
          ) {
            for (const category of selectedTool[0].allowedCategories) {
              if (categories[category]) {
                this.categoryData.push({
                  category: category,
                  name: categories[category].name,
                });
              }
            }
          } else {
            for (const [key, value] of Object.entries(categories)) {
              this.categoryData.push({ category: key, name: value.name });
            }
          }
        }
      });

    this.toolForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      product: new FormControl('', [Validators.required]),
      rootUrl: new FormControl(''),
      fieldMappings: this.formBuilder.array([]),
    });

    this.route.params
      .pipe(
        switchMap((params: Params) => {
          if (params.toolId) return this.toolService.getToolById(params.toolId);
          else {
            this.newTool = true;
            return this.environmentService.getEnvironmentById(
              params.environmentId,
            );
          }
        }),
      )
      .subscribe((result: Tool | Environment) => {
        if (this.isTool(result)) {
          this.tool = result as Tool;
          this.toolForm = new FormGroup({
            name: new FormControl(this.tool.name, [Validators.required]),
            product: new FormControl(this.tool.product, [Validators.required]),
            rootUrl: new FormControl(this.tool.rootUrl),
            fieldMappings: this.formBuilder.array([]),
          });

          this.tool.fieldMappings.forEach((mapping, index) => {
            const group = this.formBuilder.group({
              name: new FormControl(mapping.name, [Validators.required]),
              category: new FormControl(mapping.category, [
                Validators.required,
              ]),
              fields: this.formBuilder.array([]),
              toolSpecific: this.formBuilder.array([]),
            });

            const toolSpecific = group.get('toolSpecific') as FormArray;

            if (mapping.toolSpecific)
              mapping.toolSpecific.forEach((field) => {
                toolSpecific.push(
                  this.formBuilder.group({
                    [Object.keys(field)[0]]: new FormControl(
                      Object.values(field)[0],
                      [Validators.required],
                    ),
                  }),
                );
              });

            this.fieldMappings().push(group);

            const fields = this.fieldMappings()
              .at(index)
              .get('fields') as FormArray;

            this.categories[mapping.category].ccm_fields.forEach((field) => {
              const mappedField: { [ccmField: string]: string }[] =
                mapping.fields.filter((mapField) => {
                  return Object.keys(mapField)[0] === field;
                });

              fields.push(
                this.formBuilder.group({
                  [field]:
                    mappedField && mappedField.length > 0
                      ? Object.values(mappedField[0])[0]
                      : '',
                }),
              );
            });
          });
        } else {
          this.environment = result;
        }

        const selectedTool = this.data.filter((tool) => {
          return tool.name === this.tool.product;
        });

        if (selectedTool) this.customMapping = selectedTool[0].customMapping;
      });
  }

  public ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public onEdit(): void {
    this.isEditing = !this.isEditing;
  }

  public saveTool(): void {
    if (this.newTool) {
      const tool: Tool = this.toolForm.value;
      tool.environment = this.environment;

      this.toolService.createTool(tool).subscribe((tool: Tool) => {
        this.tool = tool;
      });
    } else {
      this.toolService
        .updateTool(this.tool.id, this.toolForm.value)
        .subscribe((tool: Tool) => {
          this.tool = tool;

          this.snackBarService.open('Changes saved', null, {
            duration: 5 * 1000,
          });
        });
    }
  }

  public deleteTool(): void {
    this.toolService.deleteTool(this.tool.id).subscribe(() => {
      this.router
        .navigate(['settings', 'environment'])
        .then((navigated: boolean) => {
          if (navigated) {
            this.snackBarService.open(
              'Deleted tool - ' + this.tool.name,
              null,
              {
                duration: 5 * 1000,
              },
            );
          }
        });
    });
  }

  public fieldMappings(): FormArray {
    return this.toolForm.get('fieldMappings') as FormArray;
  }

  public newFieldMapping(): FormGroup {
    const group = this.formBuilder.group({
      name: new FormControl('', [Validators.required]),
      category: new FormControl('', [Validators.required]),
      fields: this.formBuilder.array([]),
      toolSpecific: this.formBuilder.array([]),
    });

    const toolSpecific = group.get('toolSpecific') as FormArray;

    const selectedTool = this.data.filter((tool) => {
      return tool.name === this.toolForm.get('product').value;
    });

    const customFields = selectedTool[0]?.toolSpecific;

    if (customFields)
      customFields.forEach((field) => {
        toolSpecific.push(
          this.formBuilder.group({
            [field]: new FormControl('', [Validators.required]),
          }),
        );
      });

    return group;
  }

  // Add new field mapping, have it default to open, and close all others
  public addFieldMapping(): void {
    const newIndex = this.fieldMappings().length;
    this.fieldMappings().push(this.newFieldMapping());
    this.openIndexes.clear();
    this.openIndexes.add(newIndex);
    this.changeDetectorRef.detectChanges();
  }

  public removeFieldMapping(i: number): void {
    this.fieldMappings().removeAt(i);
  }

  public fieldMappingFields(fieldMappingIndex: number): FormArray {
    return this.fieldMappings()
      .at(fieldMappingIndex)
      .get('fields') as FormArray;
  }

  public toolSpecificFields(fieldMappingIndex: number): FormArray {
    return this.fieldMappings()
      .at(fieldMappingIndex)
      .get('toolSpecific') as FormArray;
  }

  public populateCategoryFields(value: string, i: number): void {
    const fields = this.fieldMappings().at(i).get('fields') as FormArray;
    fields.clear();
    this.categories[value].ccm_fields.forEach((field) => {
      fields.push(
        this.formBuilder.group({
          [field]: '',
        }),
      );
    });
  }

  public getReadableFieldName(key: string): string {
    return fields[key].name;
  }

  public getFieldDescription(key: string): string {
    return fields[key].description;
  }

  public getFieldName(field: AbstractControl): string {
    return Object.keys(field.value)[0];
  }

  public testUrl(): void {
    const url: string = this.toolForm.value.rootUrl;
    window.open(url, '_blank');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueChange(event: any): void {
    const selectedTool = this.data.filter((tool) => {
      return tool.name === event;
    });

    if (selectedTool) {
      const mappings = this.toolForm.get('fieldMappings') as FormArray;
      mappings.clear();
      this.customMapping = selectedTool[0].customMapping;
      this.categoryData = [];
      if (
        selectedTool[0].allowedCategories &&
        selectedTool[0].allowedCategories.length > 0
      ) {
        for (const category of selectedTool[0].allowedCategories) {
          if (categories[category])
            this.categoryData.push({
              category: category,
              name: categories[category].name,
            });
        }
      } else {
        for (const [key, value] of Object.entries(categories)) {
          this.categoryData.push({ category: key, name: value.name });
        }
      }
    }
  }

  public getReadableName(name: string): string {
    name = name.replaceAll('_', ' ');
    name = name.replace(/([A-Z])/g, ' $1');
    return name;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public isTool(object: any): object is Tool {
    return 'product' in object;
  }

  public applyDefaults(i): void {
    const controls = this.fieldMappingFields(i).controls;
    controls.forEach((control) => {
      const defaultField = this.getFieldName(control);
      // Apply tool specific default CCM field mapping for field if it exists, else fall back to tool agnostic default
      const toolCustomField = toolFactory(this.toolForm.value.product)
        .toolDefaultCCMFieldMappings[defaultField];
      if (toolCustomField)
        control.setValue({ [defaultField]: toolCustomField });
      else control.setValue({ [defaultField]: defaultField });
    });
  }

  // Method to handle opening a field mapping
  public onToggle(index: number, event: Event): void {
    const isOpen = (event.target as HTMLDetailsElement).open;
    if (isOpen) {
      this.openIndexes.add(index);
    } else {
      this.openIndexes.delete(index);
    }
  }
}
